// frontend/src/contexts/AuthContext.tsx
// ВЕРСИЯ: 2.0.1 ULTIMATE (2026-01-30)
// ИСПРАВЛЕНИЯ:
// - v2.0.1: ✅ timeout 5000ms для всех fetch (GPT improvement - prevent hanging)
// - v2.0.1: ✅ AbortController для cleanup pending requests (GPT improvement)
// - v2.0.1: ✅ setError в finally если err (GPT improvement)
// - v2.0.1: ✅ Совместимость token / auth_token (GPT improvement)
// - v2.0.0: ✅ КРИТИЧНО - setLoading(false) ВСЕГДА в finally
// - v2.0.0: ✅ Сохранение role/email в localStorage для fallback
// - v2.0.0: ✅ Правильные пути импортов ../config/paths

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { apiUrl, appUrl } from '../config/paths';

export interface User {
  id: number;
  email: string;
  role: 'operator' | 'client' | 'admin';
  organization_name: string;
  full_name: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshToken: () => Promise<void>;
  requestCode: (email: string, password: string) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function decodeJWT(token: string): any | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

function deriveRole(payload: any): 'operator' | 'client' | 'admin' {
  let userRole: 'operator' | 'client' | 'admin' = 'client';

  // Keycloak-style
  if (payload?.realm_access?.roles && Array.isArray(payload.realm_access.roles)) {
    if (payload.realm_access.roles.includes('admin')) userRole = 'admin';
    else if (payload.realm_access.roles.includes('operator')) userRole = 'operator';
    else if (payload.realm_access.roles.includes('client')) userRole = 'client';
    return userRole;
  }

  // Simple role field
  if (payload?.role && ['operator', 'client', 'admin'].includes(payload.role)) {
    return payload.role;
  }

  return userRole;
}

function buildUser(payload: any, fallbackEmail = ''): User {
  const role = deriveRole(payload);

  return {
    id: payload?.sub ? parseInt(String(payload.sub), 10) || 1 : 1,
    email: payload?.email || payload?.preferred_username || fallbackEmail || '',
    role,
    organization_name: payload?.organization_name || '',
    full_name: payload?.name || payload?.full_name || fallbackEmail || '',
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const persistUserMeta = useCallback((u: User | null) => {
    if (!u) {
      localStorage.removeItem('role');
      localStorage.removeItem('email');
      return;
    }
    localStorage.setItem('role', u.role);
    localStorage.setItem('email', u.email);
  }, []);

  /** ЛОГАУТ: POST /api/auth/logout → чистка → /login */
  const logout = useCallback(async () => {
    setLoading(true);

    // v2.0.1: Совместимость token / auth_token
    const refreshTokenValue =
      localStorage.getItem('refreshToken') || localStorage.getItem('refresh_token');

    let controller: AbortController | null = null; // v2.0.1: AbortController
    let timeoutId: NodeJS.Timeout | null = null;

    try {
      if (refreshTokenValue) {
        controller = new AbortController();
        timeoutId = setTimeout(() => controller?.abort(), 5000); // v2.0.1: Timeout

        await fetch(apiUrl('auth/logout'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refreshTokenValue }),
          signal: controller.signal, // v2.0.1: AbortController
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);

      // v2.0.1: Очистка обоих токенов (совместимость)
      localStorage.removeItem('token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('refresh_token');

      const currentRole = localStorage.getItem('role') || 'operator';
      persistUserMeta(null);
      setUser(null);
      setLoading(false); // v2.0.0: В finally
      if (currentRole === 'client') {
        window.location.href = appUrl('/apply');
      } else {
        window.location.href = appUrl('/login');
      }
    }
  }, [persistUserMeta]);

  /** REFRESH: POST /api/auth/refresh → новая пара токенов → checkAuth() */
  const refreshToken = useCallback(async () => {
    // v2.0.1: Совместимость token / auth_token
    const refreshTokenValue =
      localStorage.getItem('refreshToken') || localStorage.getItem('refresh_token');

    if (!refreshTokenValue) {
      await logout();
      return;
    }

    let controller: AbortController | null = null; // v2.0.1
    let timeoutId: NodeJS.Timeout | null = null;
    let localError: string | null = null; // v2.0.1: для finally

    try {
      controller = new AbortController();
      timeoutId = setTimeout(() => controller?.abort(), 5000); // v2.0.1: Timeout

      const response = await fetch(apiUrl('auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
        signal: controller.signal, // v2.0.1: AbortController
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      const newToken = data.accessToken || data.access_token;
      const newRefreshToken = data.refreshToken || data.refresh_token;

      if (!newToken) throw new Error('No access token on refresh');

      // v2.0.1: Сохранение обоих токенов (совместимость)
      localStorage.setItem('token', newToken);
      localStorage.setItem('auth_token', newToken);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('refresh_token', newRefreshToken);
      }

      const payload = decodeJWT(newToken);
      if (!payload) throw new Error('Invalid refreshed token');

      const userData = buildUser(payload);
      setUser(userData);
      persistUserMeta(userData);
    } catch (err) {
      console.error('Refresh token error:', err);
      localError = err instanceof Error ? err.message : 'Refresh error';
      await logout();
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      if (localError) setError(localError); // v2.0.1: setError в finally
      setLoading(false); // v2.0.0: В finally
    }
  }, [logout, persistUserMeta]);

  /** ПРОВЕРКА СЕССИИ: валидность JWT (exp), при необходимости — refresh */
  const checkAuth = useCallback(async () => {
    setLoading(true);
    setError(null);

    // v2.0.1: Совместимость token / auth_token
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');

    let localError: string | null = null; // v2.0.1: для finally

    try {
      if (!token) {
        setUser(null);
        persistUserMeta(null);
        return;
      }

      const payload = decodeJWT(token);
      if (!payload) throw new Error('Invalid token');

      // exp в секундах
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        await refreshToken();
        return;
      }

      const userData = buildUser(payload);
      setUser(userData);
      persistUserMeta(userData);
    } catch (err) {
      console.error('Auth check error:', err);
      localError = err instanceof Error ? err.message : 'Auth error';

      // v2.0.1: Очистка обоих токенов (совместимость)
      localStorage.removeItem('token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('refresh_token');

      setUser(null);
      persistUserMeta(null);
    } finally {
      if (localError) setError(localError); // v2.0.1: setError в finally
      setLoading(false); // v2.0.0: КРИТИЧНО - ВСЕГДА!
    }
  }, [persistUserMeta, refreshToken]);

  /** ЛОГИН: POST /api/auth/login → сохранить токены → роль → редирект */
  const login = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      setError(null);

      let controller: AbortController | null = null; // v2.0.1
      let timeoutId: NodeJS.Timeout | null = null;
      let localError: string | null = null; // v2.0.1: для finally

      try {
        controller = new AbortController();
        timeoutId = setTimeout(() => controller?.abort(), 5000); // v2.0.1: Timeout

        const response = await fetch(apiUrl('auth/login'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
          signal: controller.signal, // v2.0.1: AbortController
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || 'Ошибка авторизации');
        }

        const token = data.accessToken || data.access_token || data.token;
        const refresh = data.refreshToken || data.refresh_token;

        if (!token) throw new Error('Токен не получен от сервера');

        // v2.0.1: Сохранение обоих токенов (совместимость)
        localStorage.setItem('token', token);
        localStorage.setItem('auth_token', token);
        if (refresh) {
          localStorage.setItem('refreshToken', refresh);
          localStorage.setItem('refresh_token', refresh);
        }

        const payload = decodeJWT(token);
        if (!payload) throw new Error('Ошибка декодирования токена');

        const userData = buildUser(payload, email);
        setUser(userData);
        persistUserMeta(userData);

        setTimeout(() => {
          window.location.href = appUrl(userData.role);
        }, 100);
      } catch (err: unknown) {
        localError = err instanceof Error ? err.message : 'Ошибка авторизации';
        throw err;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
        if (localError) setError(localError); // v2.0.1: setError в finally
        setLoading(false); // v2.0.0: КРИТИЧНО - ВСЕГДА!
      }
    },
    [persistUserMeta]
  );

  /** REQUEST CODE: POST /api/auth/request-code-with-password → OTP на email */
  const requestCode = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    let controller: AbortController | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    let localError: string | null = null;

    try {
      controller = new AbortController();
      timeoutId = setTimeout(() => controller?.abort(), 5000);

      const response = await fetch(apiUrl('auth/request-code-with-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
        signal: controller.signal,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Ошибка отправки кода');
      }

      // Backend returns: { success: true, requireOtp: true, message: string, expiresAt: string }
      if (!data.success) {
        throw new Error(data?.message || 'Не удалось отправить код');
      }
    } catch (err: unknown) {
      localError = err instanceof Error ? err.message : 'Ошибка отправки кода';
      throw err;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      if (localError) setError(localError);
      setLoading(false);
    }
  }, []);

  /** VERIFY CODE: POST /api/auth/verify-code → токен → редирект */
  const verifyCode = useCallback(
    async (email: string, code: string) => {
      setLoading(true);
      setError(null);

      let controller: AbortController | null = null;
      let timeoutId: NodeJS.Timeout | null = null;
      let localError: string | null = null;

      try {
        controller = new AbortController();
        timeoutId = setTimeout(() => controller?.abort(), 5000);

        const response = await fetch(apiUrl('auth/verify-code'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), code }),
          signal: controller.signal,
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || 'Неверный код');
        }

        // Backend returns: { success, access_token, token_type, expires_in, user }
        const token = data.access_token || data.accessToken || data.token;

        if (!token) throw new Error('Токен не получен от сервера');

        // Сохранение токенов (refresh_token не приходит на verify-code)
        localStorage.setItem('token', token);
        localStorage.setItem('auth_token', token);

        const payload = decodeJWT(token);
        if (!payload) throw new Error('Ошибка декодирования токена');

        const userData = buildUser(payload, email);
        setUser(userData);
        persistUserMeta(userData);

        setTimeout(() => {
          window.location.href = appUrl(userData.role);
        }, 100);
      } catch (err: unknown) {
        localError = err instanceof Error ? err.message : 'Ошибка проверки кода';
        throw err;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
        if (localError) setError(localError);
        setLoading(false);
      }
    },
    [persistUserMeta]
  );

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      loading,
      error,
      login,
      logout,
      checkAuth,
      refreshToken,
      requestCode,
      verifyCode,
    }),
    [user, loading, error, login, logout, checkAuth, refreshToken, requestCode, verifyCode]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
